type MockChartProps = {
  points: number[];
  color?: string;
  height?: number;
  width?: number;
  fill?: boolean;
  showAxis?: boolean;
  zeroBaseline?: boolean;
};

/**
 * 折れ線グラフをSVGで簡易に描画 (チャートライブラリを使わない)
 */
export const MockChart = ({
  points,
  color = "#2563eb",
  height = 240,
  width = 600,
  fill = true,
  showAxis = false,
  zeroBaseline = false,
}: MockChartProps) => {
  if (points.length === 0) return null;
  const padX = 8;
  const padY = 8;
  const max = Math.max(...points);
  const min = zeroBaseline ? 0 : Math.min(...points);
  const range = Math.max(1, max - min);
  const stepX = (width - padX * 2) / Math.max(1, points.length - 1);

  const mapped = points.map((p, i) => {
    const x = padX + stepX * i;
    const y = padY + (height - padY * 2) * (1 - (p - min) / range);
    return [x, y] as const;
  });

  const polyline = mapped.map(([x, y]) => `${x.toFixed(1)},${y.toFixed(1)}`).join(" ");
  const areaPath =
    `M ${mapped[0][0]},${height - padY} L ` +
    mapped.map(([x, y]) => `${x.toFixed(1)},${y.toFixed(1)}`).join(" L ") +
    ` L ${mapped[mapped.length - 1][0]},${height - padY} Z`;

  const safeId =
    "mc_" +
    color.replace(/[^a-z0-9]/gi, "") +
    "_" +
    Math.abs(points.reduce((s, v, i) => s + Math.round(v) * (i + 1), 0)).toString(36);

  return (
    <svg
      className="mock-line-chart"
      viewBox={`0 0 ${width} ${height}`}
      preserveAspectRatio="none"
      width="100%"
      height={height}
    >
      <defs>
        <linearGradient id={safeId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.25" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>

      {showAxis &&
        [0.25, 0.5, 0.75].map((r) => (
          <line
            key={r}
            x1={padX}
            x2={width - padX}
            y1={padY + (height - padY * 2) * r}
            y2={padY + (height - padY * 2) * r}
            stroke="#f1f5f9"
            strokeWidth={1}
          />
        ))}

      {fill && <path d={areaPath} fill={`url(#${safeId})`} />}
      <polyline
        points={polyline}
        fill="none"
        stroke={color}
        strokeWidth={2.4}
        strokeLinejoin="round"
        strokeLinecap="round"
      />
      {mapped.length <= 12 &&
        mapped.map(([x, y], i) => (
          <circle key={i} cx={x} cy={y} r={2.5} fill="#fff" stroke={color} strokeWidth={1.6} />
        ))}
    </svg>
  );
};

type DonutProps = {
  data: { label: string; value: number; color: string }[];
  size?: number;
  thickness?: number;
};

/**
 * ドーナツチャート (SVG)
 */
export const MockDonut = ({ data, size = 180, thickness = 24 }: DonutProps) => {
  const total = data.reduce((s, d) => s + d.value, 0);
  const radius = size / 2 - thickness / 2;
  const cx = size / 2;
  const cy = size / 2;
  const circumference = 2 * Math.PI * radius;

  let offset = 0;
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      <circle cx={cx} cy={cy} r={radius} fill="none" stroke="#f1f5f9" strokeWidth={thickness} />
      {data.map((d, i) => {
        const len = (d.value / total) * circumference;
        const dash = `${len} ${circumference - len}`;
        const el = (
          <circle
            key={i}
            cx={cx}
            cy={cy}
            r={radius}
            fill="none"
            stroke={d.color}
            strokeWidth={thickness}
            strokeDasharray={dash}
            strokeDashoffset={-offset}
            strokeLinecap="butt"
            transform={`rotate(-90 ${cx} ${cy})`}
          />
        );
        offset += len;
        return el;
      })}
      <text
        x={cx}
        y={cy - 4}
        textAnchor="middle"
        fill="#111827"
        fontSize="22"
        fontWeight="800"
      >
        {total.toLocaleString()}
      </text>
      <text
        x={cx}
        y={cy + 14}
        textAnchor="middle"
        fill="#6b7280"
        fontSize="11"
        fontWeight="600"
      >
        合計
      </text>
    </svg>
  );
};

type BarProps = {
  bars: { label: string; value: number; max: number; color?: string }[];
  height?: number;
};

/**
 * 横棒グラフ
 */
export const MockBars = ({ bars, height = 240 }: BarProps) => {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12, height }}>
      {bars.map((b, i) => (
        <div key={i}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              fontSize: 12,
              color: "#6b7280",
              marginBottom: 4,
              fontWeight: 600,
            }}
          >
            <span>{b.label}</span>
            <span style={{ color: "#111827", fontWeight: 700 }}>
              {b.value.toLocaleString()}
            </span>
          </div>
          <div className="progress">
            <div
              className="progress-bar"
              style={{
                width: `${(b.value / b.max) * 100}%`,
                background: b.color
                  ? `linear-gradient(90deg, ${b.color}, ${b.color}cc)`
                  : undefined,
              }}
            />
          </div>
        </div>
      ))}
    </div>
  );
};

type GroupedBarProps = {
  data: { label: string; values: number[] }[];
  colors: string[];
  height?: number;
};

/**
 * グループ縦棒グラフ
 */
export const MockGroupedBars = ({ data, colors, height = 240 }: GroupedBarProps) => {
  const max = Math.max(...data.flatMap((d) => d.values));
  return (
    <div style={{ display: "flex", alignItems: "flex-end", gap: 14, height, padding: "12px 0" }}>
      {data.map((d, i) => (
        <div
          key={i}
          style={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 8,
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "flex-end",
              gap: 4,
              height: "100%",
              width: "100%",
              justifyContent: "center",
            }}
          >
            {d.values.map((v, j) => (
              <div
                key={j}
                style={{
                  width: 14,
                  height: `${(v / max) * 100}%`,
                  background: colors[j % colors.length],
                  borderRadius: "6px 6px 0 0",
                }}
                title={`${v}`}
              />
            ))}
          </div>
          <span style={{ fontSize: 11, color: "#6b7280", fontWeight: 600 }}>{d.label}</span>
        </div>
      ))}
    </div>
  );
};
